/**
 * presentAccessDecision - 根据客户端 L1 预判决策展示对应 UI
 *
 * 职责（spec §7.3）：
 * - decision.allow -> 不做事（返回 false，调用方继续执行 action）
 * - decision.deny login_required -> 打开 loginModal，并在弹层内说明原因、记录 pendingAccessIntent
 * - decision.deny paywall -> 打开 paywallModal，并在弹层内说明原因
 * - decision.partial -> 截断正文 UI（chapter.unlock 专用，在 article view 里处理）
 *
 * 安全边界：
 * - 不直接修改业务状态
 * - 登录后恢复（pendingAccessIntent）的存储由调用方传入 store
 * - 只做 UI 层提示，不做服务端授权
 *
 * 返回值：
 * - true = 已处理（调用方应 return，不继续执行 action）
 * - false = 未处理，调用方继续执行
 */

import type { Root } from "./prototypeTypes";
import type { PrototypeState } from "./prototypeHydration";
import { openModal, closeAllModals } from "./navigationController";
import { showToast } from './toastController';
import type { ClientAccessDecision } from "./accessResolver";
import { ACTION_REGISTRY } from '../../domain/accessRegistry';
import type { AccessAction, ActionSpec } from "../../domain/types/accessTypes";
import { recordPendingAccessIntent } from "./pendingAccessIntent";
import { openPaywallWithLayout } from './commerceController';
import { resetAuthModalIntentNote, showAuthModalIntentNote } from './formProfileController';
import { planStore } from './planStore';
import { lowestPaidPlanWithCapability } from '../../domain/access/lowestPaidPlanWithCapability';

export interface PresentAccessOptions {
	/** 触发 action 的原始字符串（用于 pendingAccessIntent） */
	rawAction: string;
	/** 解析后的 AccessAction（可能为 null） */
	policyAction: AccessAction | null;
	/** Server-supplied target plan wins when an L1 optimistic allow reaches L3. */
	requiredPlan?: NonNullable<ActionSpec['requiredPlan']>;
	/** GitHub's minimum paid plan is a runtime D1 capability, not a static action property. */
	dynamicRequiredCapability?: 'githubDeepDive';
}

/**
 * presentAccessDecision - 展示决策结果
 *
 * @returns true 已处理 UI（调用方 return），false 未处理（调用方继续）
 */
export function presentAccessDecision(
	root: Root,
	state: PrototypeState,
	decision: ClientAccessDecision,
	opts: PresentAccessOptions,
): boolean {
	if (decision.kind === "allow") {
		return false;
	}

	if (decision.kind === "partial") {
		// partial 正文由 chapter view 自行处理截断展示，这里不拦截
		// 但若 gate=login 且未登录 -> 走 login 流程；gate=paywall 且已登录 -> 走 paywall
		if (decision.gate === "login" && state.isLoggedIn === false) {
			return presentLoginGate(root, state, opts);
		}
		if (decision.gate === "paywall" && state.isLoggedIn === true) {
			return presentPaywallGate(root, state, opts, undefined);
		}
		// 否则 partial 决策不拦截，action router 继续执行（但服务端会返回 partial 正文）
		return false;
	}

	if (decision.kind === "login") {
		return presentLoginGate(root, state, opts);
	}
	if (decision.kind === "paywall") {
		return presentPaywallGate(root, state, opts, decision.capability === 'githubDeepDive' || decision.action === 'github.deepDive.open' ? 'githubDeepDive' : undefined);
	}
	if (decision.kind === "quota") {
		openPaywallWithLayout(root, state, {
			setIntent: (targetRoot) => setPaywallPrompt(targetRoot, "You've reached this month's quota. Upgrade for more capacity."),
		});
		return true;
	}
	if (decision.kind === "disabled") {
		// This path has no actionable modal. A short transient notice remains
		// appropriate here, unlike login/paywall gates which own their copy.
		showNonBlockingNotice('This feature is currently unavailable.');
		return true;
	}

	// 未识别的 deny reason：保守拦截
	return true;
}

function presentLoginGate(
	root: Root,
	state: PrototypeState,
	opts: PresentAccessOptions,
): boolean {
	// 记录 pendingAccessIntent：登录后恢复（spec §7.5）
	if (opts.policyAction) {
		recordPendingAccessIntent({
			action: opts.policyAction,
			rawAction: opts.rawAction,
			recordedUserId: state.isLoggedIn ? state.runtime.userId : null,
		});
	}
	resetAuthModalIntentNote(root, 'login');
	openModal(root, "loginModal");
	return true;
}

function presentPaywallGate(
	root: Root,
	state: PrototypeState,
	opts: PresentAccessOptions,
	dynamicRequiredCapability: 'githubDeepDive' | undefined,
): boolean {
	closeAllModals(root);
	const catalog = planStore.getAll();
	const requiredPlan = opts.requiredPlan
		?? (opts.policyAction === 'github.deepDive.open' && catalog ? lowestPaidPlanWithCapability(catalog, 'githubDeepDive') ?? undefined : undefined)
		?? (opts.policyAction ? ACTION_REGISTRY[opts.policyAction].requiredPlan : undefined);
	const isGithubDeepDive = opts.policyAction === 'github.deepDive.open' || opts.dynamicRequiredCapability === 'githubDeepDive' || dynamicRequiredCapability === 'githubDeepDive';
	openPaywallWithLayout(root, state, {
		requiredPlan,
		dynamicRequiredCapability: opts.dynamicRequiredCapability ?? dynamicRequiredCapability,
		setIntent: (targetRoot, _targetState, refreshedRequiredPlan) => setPaywallPrompt(
			targetRoot,
			isGithubDeepDive
				? `Upgrade to ${paywallPlanLabel(refreshedRequiredPlan) ?? 'continue'} to open this GitHub Deep Dive.`
				: 'Upgrade to access this feature.',
		),
	});
	return true;
}

function paywallPlanLabel(plan: string | undefined): string | undefined {
	return plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : undefined;
}

/**
 * Access gates are already blocking, focused surfaces. Repeating their reason
 * as a page-level "Heads up" toast creates a second competing prompt,
 * especially on mobile where toasts sit at the top of the viewport.
 */
export function setLoginPrompt(root: Root, message: string): void {
	showAuthModalIntentNote(root, 'login', message);
}

export function setPaywallPrompt(root: Root, message: string): void {
	const note = root.querySelector<HTMLElement>('#paywallIntentNote');
	if (!note) return;
	note.textContent = message;
	note.style.display = '';
}

function showNonBlockingNotice(message: string): void {
	showToast({ type: 'info', message, durationMs: 3500 });
}
